import { Fragment, useState } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '@utils'
import { ChevronUp, HelpCircle } from 'lucide-react'
import { useSessionStore } from '@stores'
import { buildOrderTotals, PENDING_REFUND_HELP } from '@utils/orderTotals'

interface OrderTotalsInfoProps {
  order: {
    id: number
    currency: string
    subtotal: string
    shipping_price: string
    hidden_fees: string
    discount: string
    tax: string
    tip: string
    total_discounted_price: string
    past_balance?: string
    forward_balance?: string
    amount_paid?: string
    uncaptured_auth?: string
    store_debit?: string
    amount_voided?: string
    settled_refund_amount?: string
    pending_refund_amount?: string
    store_credit?: string
    chargeback_amount?: string
    total_remaining_balance?: string
    amount_due?: string
    [key: string]: any
  }
}

const formatCurrency = (amount: string | number | undefined, currency: string): string => {
  const num = parseFloat(String(amount ?? '0'))
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
  }).format(num)
}

interface RowProps {
  label: React.ReactNode
  value: string
  cancelled?: boolean
  pending?: boolean
  bold?: boolean
}

const Row = ({ label, value, cancelled, pending, bold }: RowProps) => (
  <div className={cn('totals-row', bold && 'is-strong')}>
    <span className="totals-label">{label}</span>
    <span className={cn('totals-value notranslate', cancelled && 'is-cancelled', pending && 'is-pending')}>
      {value}
    </span>
  </div>
)

const OrderTotalsInfo = ({ order }: OrderTotalsInfoProps) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const session = useSessionStore(s => s.session)

  if (session?.show_order_summary !== true) {
    return null
  }

  const currency = order.currency
  const groups = buildOrderTotals(order, { hiddenFeesLabel: session?.hidden_fees_label })

  return (
    <div className={cn('order-totals', isExpanded && 'is-expanded')}>
      <div className="totals-toggle-row">
        <button type="button" className="totals-toggle-btn" onClick={() => setIsExpanded(v => !v)}>
          <span>{isExpanded ? 'Hide' : 'Show'}</span> Billing Summary
          <ChevronUp className={cn('transition-transform', !isExpanded && 'rotate-180')} size={14} />
        </button>
        {!isExpanded && (
          <div className="totals-collapsed-amount">{formatCurrency(order.total_discounted_price, currency)}</div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="totals-body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] as const }}
            style={{ overflow: 'hidden' }}>
            <div className="totals-body">
              {groups.map((rows, index) => (
                <div className="totals-group" key={index}>
                  {rows.map(row => (
                    <Fragment key={row.key}>
                      {row.dividerBefore && <hr className="totals-divider" />}
                      <Row
                        label={
                          row.key === 'pending_refund_amount' ? (
                            <span className="inline-flex items-center gap-1">
                              {row.label}:
                              <span title={PENDING_REFUND_HELP}>
                                <HelpCircle size={14} />
                              </span>
                            </span>
                          ) : (
                            `${row.label}:`
                          )
                        }
                        value={`${row.negative ? '-' : ''}${formatCurrency(row.amount, currency)}`}
                        cancelled={row.tone === 'cancelled'}
                        pending={row.tone === 'pending'}
                        bold={row.strong}
                      />
                    </Fragment>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default OrderTotalsInfo
